// Клиент realtime: один WebSocket на вкладку, подписки на комнаты проектов
// с ref-count (несколько компонентов могут подписаться на один проект).
// Сервер сам шлёт события; мы только слушаем. Реконнект с экспоненциальным backoff.

export interface RealtimeEvent {
  type: string
  [key: string]: unknown
}

type Handler = (event: RealtimeEvent) => void

const handlers = new Set<Handler>()
// projectId → число активных подписчиков (компонентов).
const subCounts = new Map<string, number>()

let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let backoff = 1000

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/ws`
}

function send(message: unknown): void {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message))
  }
}

function ensureSocket(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }
  const ws = new WebSocket(wsUrl())
  socket = ws

  ws.addEventListener('open', () => {
    backoff = 1000
    // Восстанавливаем все активные подписки после (ре)коннекта.
    for (const projectId of subCounts.keys()) {
      send({ type: 'subscribe', projectId })
    }
  })

  ws.addEventListener('message', (event) => {
    let data: RealtimeEvent | null = null
    try {
      data = JSON.parse(event.data)
    } catch {
      return
    }
    if (!data || typeof data.type !== 'string') {
      return
    }
    for (const handler of handlers) {
      handler(data)
    }
  })

  ws.addEventListener('close', () => {
    socket = null
    scheduleReconnect()
  })

  ws.addEventListener('error', () => {
    ws.close()
  })
}

function scheduleReconnect(): void {
  if (reconnectTimer != null) {
    return
  }
  // Нет ни подписок, ни слушателей — соединение не нужно.
  if (subCounts.size === 0 && handlers.size === 0) {
    return
  }
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    backoff = Math.min(backoff * 2, 15000)
    ensureSocket()
  }, backoff)
}

export const realtime = {
  subscribe(projectId: string): void {
    const count = subCounts.get(projectId) ?? 0
    subCounts.set(projectId, count + 1)
    ensureSocket()
    if (count === 0) {
      send({ type: 'subscribe', projectId })
    }
  },

  unsubscribe(projectId: string): void {
    const count = subCounts.get(projectId) ?? 0
    if (count <= 1) {
      subCounts.delete(projectId)
      send({ type: 'unsubscribe', projectId })
    } else {
      subCounts.set(projectId, count - 1)
    }
  },

  on(handler: Handler): () => void {
    handlers.add(handler)
    ensureSocket()
    return () => {
      handlers.delete(handler)
    }
  }
}
